import { Component, forwardRef, model, OnInit, signal } from '@angular/core';
import { CountryModel } from '../../core/models/data/country.model';
import { LocationService } from '../../core/services/location.service';
import { PaginatedListModel } from '../../core/models/service/paginated-list.model';
import { ListConstants } from '../../core/constants/lists.constants';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonChecks } from '../../core/utilities/common-checks.utilities';
import { FormValidatorMessages } from '../../core/enums/forms.enums';
import { BaseInputComponentClass } from '../../core/base-classes/base-input-component-class';
import { ServiceResultModel } from '../../core/models/service/service-result.model';
import { CountryAlpha2Codes } from '../../core/enums/data.enums';

@Component({
  selector: 'ms-country-dropdown',
  imports: [],
  templateUrl: './country-dropdown.html',
  styleUrl: './country-dropdown.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountryDropdown),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CountryDropdown),
      multi: true,
    },
  ]
})
export class CountryDropdown extends BaseInputComponentClass implements OnInit, ControlValueAccessor {

  public selectedCountry = model<CountryModel | undefined>(undefined);
  public countriesList = signal<CountryModel[] | undefined>(undefined);

  // The countries the API holds state/province subdivisions for. Anything outside this list has no
  // state to pick, and the address verification service derives the country from the state (DEV-716).
  private readonly supportedCountryAlpha2Codes: string[] = [
    CountryAlpha2Codes.UnitedStatesOfAmerica,
    CountryAlpha2Codes.Canada,
    CountryAlpha2Codes.Mexico,
    CountryAlpha2Codes.UnitedKingdom,
    CountryAlpha2Codes.CostaRica,
    CountryAlpha2Codes.DominicanRepublic,
    CountryAlpha2Codes.Bahamas,
    CountryAlpha2Codes.BritishVirginIslands,
    CountryAlpha2Codes.CaymanIslands
  ];

  constructor(private locationsService: LocationService) {
    super();
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.loadCountries();
  }

  public async loadCountries(): Promise<void> {
    this.isLoading = true;

    try {
      let response: ServiceResultModel<PaginatedListModel<CountryModel>> =
        await this.locationsService.searchCountries({ alpha2Codes: this.supportedCountryAlpha2Codes }, ListConstants.COUNTRIES_ORDERBY_DEFAULT(), 0, 150);

      if (response?.data?.data) {
        this.countriesList.set(response.data?.data);
      }
    } catch (error) {
      console.error('Error loading countries:', error);
    } finally {
      this.isLoading = false;
    }
  }

  public selectCountry(country: CountryModel | undefined): void {
    this.selectedCountry.set(country);
    this.onChange(country);
  }

  public writeValue(value: any): void {
    this.selectedCountry.set(value);
  }

  public get isValid(): boolean {
    return this.touched && !CommonChecks.isNullOrUndefined(this.selectedCountry)
      && this.ngControl?.control?.errors == null;
  }

  public getErrors(): string[] {
      const errors: string[] = [];
      const controlErrors = this.ngControl?.control?.errors;

      if (controlErrors) {
        if (controlErrors['required']) {
          errors.push(controlErrors['required'].message || FormValidatorMessages.CountryRequired);
        }
      }

      return errors;
  }
}
